import { defineStore } from 'pinia';
import { configureEcho, useEcho, echo } from "@laravel/echo-vue";
import { useAuthStore } from '@/stores/auth';
import { useTrackingStore } from '@/stores/tracking';
import { useDepositStore } from '@/stores/deposit';
import { useBonusStore } from '@/stores/bonus';
import consola from 'consola';
import type {
    CasinoTransactionSource,
    TransactionPayload,
    BetTransactionSource,
    JwtPayload,
} from '@/types/transactions';

interface BalancePayload {
    balance: number;
}

interface BonusPayload {
    id: string;
    active: boolean;
    amount_current: number;
    amount_rollover: number;
    amount_won: number;
    balance: number;
}

export const useWebsocketStore = defineStore('websocket', () => {
    const authStore = useAuthStore();
    const trackingStore = useTrackingStore();
    const depositStore = useDepositStore();
    const bonusStore = useBonusStore();
    const runtimeConfigs = useRuntimeConfig();

    const userId = ref<string | null>(null);
    const activeRollover = ref<BonusPayload | null>(null);

    const handleBalanceUpdate = async (payload: BalancePayload): Promise<void> => {
        /* @__PURE__ */ consola.info('handleBalanceUpdate', payload);

        // Now we set user updated balance
        authStore.setBalance(payload.balance);

        // Get the current token and check expiration
        const jwtCookie = useCookie("jwt").value;
        const decodedToken = parseJwt(jwtCookie);

        if (!decodedToken) {
            return;
        }

        const now = Date.now();
        const tokenExpirationMs = decodedToken.exp * 1000;
        const FIVE_MINUTES_MS = 5 * 60 * 1000;
        const timeUntilExpiration = tokenExpirationMs - now;

        // If the token is about to expire, refresh it
        if (timeUntilExpiration <= FIVE_MINUTES_MS) {
            await authStore.getUser();
        }
    };

    const handleBonusUpdate = (payload: BonusPayload): void => {
        /* @__PURE__ */ consola.info('handleBonusUpdate', payload);
        authStore.balance.bonus = payload.balance;

        if (bonusStore.activeRollover && activeRollover.value?.id === payload.id) {
            activeRollover.value = {
                ...activeRollover.value,
                active: payload.active,
                amount_current: payload.amount_current,
                amount_rollover: payload.amount_rollover,
                amount_won: payload.amount_won,
                balance: payload.balance
            };
        }
    };

    const handleUserTransactionsUpdate = async (payload: TransactionPayload): Promise<void> => {
        /* @__PURE__ */ consola.info('handleUserTransactionsUpdate', payload);

        // Handling deposit transactions
        if (payload.source_type === 'App\\Models\\Deposit') {
            // Use the source object for deposit details
            const deposit = payload.source as { id: string; amount?: number; status: number; occurrence?: number };
            if (!deposit || !deposit.id) {
                /* @__PURE__ */ consola.error('Deposit or deposit.id is missing in payload:', payload);
                return;
            }

            // Status 0 = pending, Status 2 = approved
            if (deposit.status === 0) {
                await trackingStore.actions.sendEvent('deposit', {
                    id: deposit.id,
                    amount: (deposit.amount ?? 0) / 100,
                    currency: 'BRL',
                    status: 'pending',
                });
            } else if (deposit.status === 2) {
                await trackingStore.actions.sendEvent('deposit_approved', {
                    id: deposit.id,
                    amount: (deposit.amount ?? 0) / 100,
                    currency: 'BRL',
                    status: 'approved',
                    occurrence: deposit.occurrence ?? 0,
                });
            }

        }

        // Handling casino transactions
        if (payload.source_type === 'App\\Models\\CasinoTransaction' && payload.status === 2 && payload.type === 0) {
            const casinoSource = payload.source as CasinoTransactionSource;
            console.log(casinoSource)
            if (casinoSource && casinoSource.casino_game) {
                trackingStore.actions.sendEvent('bet_transaction', {
                    value: casinoSource.amount,
                    virtual_currency_name: 'BRL',
                    game_category: 'Cassino',
                    item_name: casinoSource.casino_game.name,
                });
            }
        }

        // Handling sports bet transactions
        if (payload.source_type === 'App\\Models\\BetTransaction' && payload.status === 2 && (payload.source as BetTransactionSource)?.type === 0) {
            const betSource = payload.source as BetTransactionSource;
            trackingStore.actions.sendEvent('bet_transaction', {
                value: payload.amount,
                virtual_currency_name: 'BRL',
                game_category: 'Esportes',
                item_name: `${betSource.market.sport_name} - ${betSource.market.full_name}`,
            });
        }


    };

    const initializeWebsocket = (): void => {
        if (!authStore.isLoggedIn) {
            return;
        }

        const user = authStore.user;
        if (!user) {
            return;
        }

        userId.value = user.id!;
        const jwtCookie = useCookie('jwt').value;

        try {
            configureEcho({
                broadcaster: "pusher",
                key: runtimeConfigs.public.PUSHER_APP_KEY,
                cluster: 'mt1',
                wsHost: runtimeConfigs.public.PUSHER_HOST,
                wsPort: Number(runtimeConfigs.public.PUSHER_PORT),
                forceTLS: runtimeConfigs.public.PUSHER_TLS === 'true',
                enabledTransports: ["ws", "wss"],
                authEndpoint: `${location.origin}/api/broadcasting/auth`,
                auth: {
                    headers: {
                        Authorization: `Bearer ${jwtCookie}`,
                        Accept: 'application/json'
                    }
                }
            });

            /* @__PURE__ */ consola.success(`%cWebsocket initialized%c`,
                `color: #FFD700; font-weight: bold;`,
                "color: inherit;");

            echo().private(`users.${userId.value}`).listen('UserUpdateEvent', (payload: BalancePayload) => {
                handleBalanceUpdate(payload);
            }).subscribed(() => {
                /* @__PURE__ */ consola.ready(`%cWebsocket channel:%c users.${userId.value} subscribed`,
                    `color: #FFD700; font-weight: bold;`,
                    "color: inherit;");
            });

            echo().private(`transactions.user.${userId.value}`).listen('UserTransactionEvent', (payload: TransactionPayload) => {
                handleUserTransactionsUpdate(payload);
            }).subscribed(() => {
                /* @__PURE__ */ consola.ready(`%cWebsocket channel:%c transactions.user.${userId.value} subscribed`,
                    `color: #FFD700; font-weight: bold;`,
                    "color: inherit;");
            });

            if (activeRollover.value && Object.keys(activeRollover.value).length) {
                echo().private(`bonus.${activeRollover.value?.id}`).listen('UserRolloverUpdateEvent', (payload: BonusPayload) => {
                    handleBonusUpdate(payload);
                }).subscribed(() => {
                    /* @__PURE__ */ consola.ready(`%cWebsocket channel:%c bonus.${activeRollover.value?.id} subscribed`,
                        `color: #FFD700; font-weight: bold;`,
                        "color: inherit;");
                });
            }

        } catch (error) {
            /* @__PURE__ */ consola.error('Error configuring Echo:', error);
        }
    };

    // Helper function to parse JWT
    const parseJwt = (token: string): JwtPayload | null => {
        try {
            return JSON.parse(atob(base64urlToBase64(token.split('.')[1])));
        } catch (e) {
            return null;
        }
    };

    const base64urlToBase64 = (base64url: string): string => {
        base64url = base64url.replace(/-/g, '+').replace(/_/g, '/');

        switch (base64url.length % 4) {
            case 2:
                base64url += '==';
                break;
            case 3:
                base64url += '=';
                break;
        }

        return base64url;
    };

    return {
        initializeWebsocket
    };
}); 